import * as React from 'react';
import { ReactElement } from 'react';
import TableCell from '@mui/material/TableCell';
import { DataCollectionData } from 'benchmarking-domain/data-collection';
import { BarchartDiagramAttributes, QuestionnaireSectionRowDiagram } from 'benchmarking-domain/questionnaire';
import { Bar } from 'react-chartjs-2';

import { DataCollectionHelper } from '../../../helpers/data-collection';
import { NumberHelper } from '../../../helpers/number';

export interface QuestionnaireSectionTableDiagramProps {
  valueIdPrefix?: string;
  diagram: QuestionnaireSectionRowDiagram;
  dataCollection?: DataCollectionData;
  globalVariables: DataCollectionData;
}

export function QuestionnaireSectionTableDiagram({
  valueIdPrefix,
  diagram,
  dataCollection,
  globalVariables,
}: QuestionnaireSectionTableDiagramProps): React.JSX.Element {
  const defaultColor = '#2e96ff';
  const greenColor = '#02b2af';
  const redColor = '#d8bcbe';

  const formatValue = (value: [number, number] | number | null, unit: string | undefined) => {
    if (value == null) {
      return 'no value';
    }

    let valueToDisplay;

    if (typeof value === 'number') {
      valueToDisplay = value;
    } else {
      valueToDisplay = DataCollectionHelper.calculateExpression(
        {
          operation: 'sub',
          arguments: [value.at(1)!, value.at(0)!],
        },
        globalVariables,
        dataCollection,
        valueIdPrefix
      ) as number;
    }

    return NumberHelper.formatNumber(valueToDisplay) + unit;
  };

  const convertToDisplay = (diagram: QuestionnaireSectionRowDiagram) => {
    let child: ReactElement = <></>;

    const colSpan = diagram.colSpan;
    let align: 'inherit' | 'left' | 'center' | 'right' | 'justify' = 'center';

    if (diagram.type === 'barchart') {
      const attr = diagram.attr as BarchartDiagramAttributes;

      const data: ([number, number] | null)[] = [];
      const backgroundColor: string[] = [];
      const labels = [];
      const isWaterfall = attr.subType === 'waterfall';

      let prevValue = 0;

      for (const bar of attr.bars) {
        labels.push(bar.label);

        const startValueRef = bar.startValue ?? (isWaterfall ? prevValue : 0);
        const startValue = DataCollectionHelper.getRefValue(
          startValueRef,
          globalVariables,
          dataCollection,
          valueIdPrefix
        );
        const value = DataCollectionHelper.getRefValue(bar.value, globalVariables, dataCollection, valueIdPrefix);

        if (typeof startValue === 'number' && typeof value === 'number') {
          const numValue = value as number;
          const color = bar.color ?? (numValue >= 0 ? greenColor : redColor);

          const finalValue = DataCollectionHelper.calculateExpression(
            {
              operation: 'add',
              arguments: [startValue, numValue],
            },
            globalVariables,
            dataCollection,
            valueIdPrefix
          ) as number;

          data.push([startValue, finalValue]);
          backgroundColor.push(color);

          prevValue = finalValue;
        } else {
          data.push(null);
          backgroundColor.push(defaultColor);
        }
      }

      child = (
        <Bar
          options={{
            responsive: true,
            plugins: {
              legend: {
                display: false,
              },
              datalabels: {
                formatter: (value) => formatValue(value, attr.unit),
              },
              tooltip: {
                boxPadding: 5,
                position: 'average',
                callbacks: {
                  label: (item) => {
                    return formatValue([item.parsed._custom?.start!, item.parsed._custom?.end!], attr.unit);
                  },
                },
              },
            },
          }}
          data={{
            labels,
            datasets: [{ backgroundColor, data }],
          }}
        />
      );
    }

    return (
      <TableCell colSpan={colSpan} align={align}>
        {child}
      </TableCell>
    );
  };

  return <>{convertToDisplay(diagram)}</>;
}
